/* eslint-disable no-unused-expressions,local-rules/no-dynamic-classname */
import {
	memo,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PropTypes from "prop-types";
import { useField, useFormikContext } from "formik";
import { FormattedDate, FormattedMessage } from "react-intl";
import "./DateCalendarInput.scss";
import classNames from "classnames";
import AppGlobalsContext from "app/AppGlobalsContext";
import { sendTagOnPriceCalendarDateClicked } from "app/utils/analytics";
import Calendar from "react-calendar";
import { isBefore, isSameDay } from "date-fns";

import {
	autoUpdate,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useFocus,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Button } from "app/components/ui/Button/Button";
import { DATE_INPUT_TYPE } from "app/constants";
import {
	formatDayArabicCalendar,
	formatMonthForArabicCalendar,
	isSaudiArabiaShop,
	getMonthsYearsInDateInterval,
} from "app/utils/utils";
import isEmpty from "lodash/isEmpty";
import compact from "lodash/compact";
import CalendarMonthHeadline from "app/pages/.shared/CalendarList/CalendarMonthHeadline";
import CalendarList from "app/pages/.shared/CalendarList/CalendarList";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { ArrowLeftIcon, ArrowRightIcon } from "app/icons/navigation";
import { CrossCircleFilledIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";
import { FlightOutboundFilledIcon } from "app/icons/travel";

// Fixed height for the isCompact popover calendar (CalendarList sizes itself as
// window.innerHeight - heightOffset, so this derives the offset needed for a fixed height instead
// of the full-viewport drawer it was originally built for).
const COMPACT_CALENDAR_HEIGHT = 400;

const DateCalendarInput = ({
	id,
	className,
	departureDateMin,
	departureDateMax,
	checkDateAvailability = () => {},
	onFocus = () => {},
	isCalendarDisabled,
	popperOffset = [],
	calendarDisabledView,
	updateFloatingPositionReference = {},
	displayCalendarIcon = true,
	loading,
	departureDateRef = {},
	endDateRef = {},
	selectRef = {},
	departureLabel = {},
	returnLabel = {},
	isAutoScrollActive,
	popoverWidth = 1024,
	isCompact = false,
	"data-testid": dataTestId,
	name,
}) => {
	const { shop, resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const initialState = {
		isDateClicked: {
			departure: false,
			end: false,
		},
		isDateCleaned: {
			departure: false,
			end: false,
		},
		calendarKey: 0,
		startDate: new Date(),
	};
	const [state, setState] = useState(initialState);

	const shouldOpenNextMonth = useCallback(
		dateValue => {
			if (!dateValue || !state.startDate) {
				return false;
			}
			const rightMonthStartDate = new Date(state.startDate);
			rightMonthStartDate.setMonth(rightMonthStartDate.getMonth() + 1);
			return (
				dateValue.getFullYear() === rightMonthStartDate.getFullYear() &&
				dateValue.getMonth() === rightMonthStartDate.getMonth() &&
				dateValue.getDate() >= 15
			);
		},
		[state.startDate]
	);

	const [departureField, departureMeta, departureHelpers] = useField(`${name}.departureDate`);
	const [endField, endMeta, endHelpers] = useField(`${name}.endDate`);

	const [open, setOpen] = useState(false);
	// Remember the previous popover state to detect reopen events.
	const wasOpenRef = useRef(false);
	// Stores the actual date clicked by the user (before react-calendar auto-sorts the range).
	const lastClickedDateRef = useRef(null);
	// Measures the closed-state field container so the isCompact popover can match its width.
	// Only captured while closed: once open, __container also holds the floating-input-container
	// as an extra flex sibling, which inflates its offsetWidth beyond the actual field width.
	const containerRef = useRef(null);
	const closedContainerWidthRef = useRef(null);
	// Width from the field's left edge to the moteur's right edge, so the open card spans the
	// whole engine (covering the search button) instead of being capped to the 287px field.
	const desktopFloatingWidthRef = useRef(null);

	const { isSubmitting, setErrors, setFieldValue } = useFormikContext();

	const departureDateMinLocal = new Date(departureDateMin);
	const departureDateMaxLocal = new Date(departureDateMax);

	const minDate =
		departureDateMin &&
		new Date(
			departureDateMinLocal.getUTCFullYear(),
			departureDateMinLocal.getUTCMonth(),
			departureDateMinLocal.getUTCDate()
		);
	const maxDate =
		departureDateMax &&
		new Date(
			departureDateMaxLocal.getUTCFullYear(),
			departureDateMaxLocal.getUTCMonth(),
			departureDateMaxLocal.getUTCDate()
		);

	useEffect(() => {
		const departureDateMinLocal = new Date(departureDateMin);

		if (departureDateMinLocal) {
			setState(prevState => ({
				...prevState,
				startDate: new Date(
					departureDateMinLocal.getUTCFullYear(),
					departureDateMinLocal.getUTCMonth(),
					departureDateMinLocal.getUTCDate()
				),
			}));
		}
	}, [departureDateMin]);

	// Compute the left month to display when both departure and return are already known.
	const stayStartDate = useMemo(() => {
		if (!departureField?.value || !endField?.value) {
			return null;
		}

		const departureDate = new Date(departureField.value);
		const departureMonthStart = new Date(
			departureDate.getFullYear(),
			departureDate.getMonth(),
			1
		);

		const endDate = new Date(endField.value);
		// Number of full months between departure and return
		const monthsDiff =
			endDate.getFullYear() * 12 +
			endDate.getMonth() -
			(departureDate.getFullYear() * 12 + departureDate.getMonth());

		if (monthsDiff <= 1) {
			// Stay fits in one double-view: keep the departure month on the left
			return departureMonthStart;
		}

		// Otherwise, align the left month so that the right calendar displays the return month
		return new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
	}, [departureField?.value, endField?.value]);

	useEffect(() => {
		// Detect transition from closed -> open.
		const isOpening = open && !wasOpenRef.current;

		// When reopening the calendar with prefilled dates, jump directly to the stay months.
		if (isOpening && stayStartDate) {
			setState(prevState => {
				const prevStart = prevState.startDate;
				if (
					prevStart &&
					prevStart.getFullYear() === stayStartDate.getFullYear() &&
					prevStart.getMonth() === stayStartDate.getMonth()
				) {
					return prevState;
				}

				return {
					...prevState,
					startDate: stayStartDate,
				};
			});
		}

		wasOpenRef.current = open;
	}, [open, stayStartDate]);

	// Capture the actual clicked date before react-calendar auto-sorts the range.
	// onClickDay fires before onChange, so the ref is always up-to-date.
	const handleClickDay = useCallback(date => {
		lastClickedDateRef.current = date;
		sendTagOnPriceCalendarDateClicked(date);
	}, []);

	const handleDateSelect = useCallback(
		date => {
			const clickedDate = lastClickedDateRef.current;
			const selectedDepartureDate = date?.[0];

			// Direct anterior detection: compare the actual clicked date with current departure.
			const isAnteriorClick =
				clickedDate &&
				departureField?.value &&
				!endField?.value &&
				(isBefore(clickedDate, new Date(departureField.value)) ||
					isSameDay(clickedDate, new Date(departureField.value)));

			if (isAnteriorClick) {
				// Anterior/same date click → set new departure date
				departureHelpers.setValue(date[0]);
				departureHelpers.setTouched(false);
				endHelpers.setTouched(true);
				endHelpers.setValue("");
				setState(prevState => ({
					...prevState,
					isDateClicked: {
						...prevState.isDateClicked,
						departure: false,
					},
				}));
			} else if (date[0] && !departureField?.value) {
				// if departure date is not selected
				departureHelpers.setValue(date[0]);
				departureHelpers.setTouched(false);
				setState(prevState => ({
					...prevState,
					isDateClicked: {
						...prevState.isDateClicked,
						departure: false,
					},
				}));
				endHelpers.setTouched(true);
				endHelpers.setValue("");
			} else if (date[0] && !date[1] && departureField?.value && !endField?.value) {
				// if departure date is selected but not the return date
				endHelpers.setValue(date[0]);
			} else if (date[0] && departureField?.value && endField.value) {
				// if both departure and return dates are selected
				departureHelpers.setValue(date[0]);
				endHelpers.setValue("");
			}

			// set the return date if it is selected and not yet set
			if (!isAnteriorClick && date[1] && !endField?.value) {
				endHelpers.setValue(date[1]);
			}
			// clear errors if both dates are selected
			if (date[0] && date[1] && (departureMeta.error || endMeta.error)) {
				departureHelpers.setError();
				endHelpers.setError();
			}

			if (state.isDateCleaned.departure) {
				setState(prevState => ({
					...prevState,
					isDateCleaned: {
						...prevState.isDateCleaned,
						departure: false,
					},
				}));
			}
			if (state.isDateCleaned.end) {
				setState(prevState => ({
					...prevState,
					isDateCleaned: {
						...prevState.isDateCleaned,
						end: false,
					},
				}));
			}

			// If the user picks a late date (>=15) in the right-hand month,
			// immediately shift the calendar to display the next month for the return selection.
			if (selectedDepartureDate && !date[1] && shouldOpenNextMonth(selectedDepartureDate)) {
				setState(prevState => {
					const nextStartDate = new Date(prevState.startDate);
					nextStartDate.setMonth(nextStartDate.getMonth() + 1);
					return {
						...prevState,
						startDate: nextStartDate,
					};
				});
			}

			// force calendar update by changing the key when dates is selected
			// Skip for anterior clicks: preserves react-calendar's internal hover state
			if (!isAnteriorClick) {
				if (
					(date[0] && date[1]) ||
					(date[0] && !date[1] && departureField?.value && !endField?.value)
				) {
					setState(prevState => ({
						...prevState,
						calendarKey: prevState.calendarKey + 1,
					}));
				}
			}
		},
		[
			departureHelpers,
			endHelpers,
			endField?.value,
			departureField?.value,
			departureMeta.error,
			endMeta.error,
			state.isDateCleaned.departure,
			state.isDateCleaned.end,
			shouldOpenNextMonth,
		]
	);

	// CalendarList (isCompact) gives one clicked date at a time instead of a react-calendar range,
	// so the departure/return progression (1st click = departure, 2nd = return) has to be derived
	// here — same progression as DepartureDateSidePanelContent's own handleDateSelect, written
	// directly to the Formik fields instead of local state.
	const handleCompactDateSelect = useCallback(
		clickedDate => {
			const currentDeparture = departureField?.value;
			const currentEnd = endField?.value;

			if (currentDeparture && currentEnd) {
				departureHelpers.setValue(clickedDate);
				departureHelpers.setTouched(false);
				endHelpers.setValue("");
				endHelpers.setTouched(true);
				return;
			}

			if (currentDeparture && !currentEnd) {
				const departureDate = new Date(currentDeparture);

				if (isBefore(clickedDate, departureDate) || isSameDay(clickedDate, departureDate)) {
					departureHelpers.setValue(clickedDate);
					return;
				}

				endHelpers.setValue(clickedDate);
				endHelpers.setTouched(false);
				return;
			}

			departureHelpers.setValue(clickedDate);
			departureHelpers.setTouched(false);
			endHelpers.setTouched(true);
		},
		[departureField?.value, endField?.value, departureHelpers, endHelpers]
	);

	const isDepartureDateTouched = isMobile
		? Boolean(departureField.value)
		: Boolean(
				(departureMeta.touched && !departureMeta.error) ||
				state.isDateClicked.departure ||
				departureField.value
			);

	const isEndDateTouched = isMobile
		? Boolean(endField.value)
		: Boolean((endMeta.touched && !endMeta.error) || state.isDateClicked.end || endField.value);

	const inputClassNameDeparture = classNames(
		"date-calendar-input date-calendar-input__departure",
		className,
		{
			"date-calendar-input__departure--opened":
				open &&
				((departureMeta.touched && !departureMeta.error) || state.isDateClicked.departure),
			"date-calendar-input__departure--touched": isDepartureDateTouched,
			"date-calendar-input__departure--error": departureMeta.error,
			"date-calendar-input--has-value": Boolean(departureField.value),
		}
	);

	const inputClassNameReturn = classNames(
		"date-calendar-input date-calendar-input__return ",
		className,
		{
			"date-calendar-input__return--opened":
				open && ((endMeta.touched && !endMeta.error) || state.isDateClicked.end),
			"date-calendar-input__return--touched": isEndDateTouched,
			"date-calendar-input__return--error": endMeta.error,
			"date-calendar-input--has-value": Boolean(endField.value),
		}
	);

	const resetDuration = useCallback(() => {
		setFieldValue("duration", undefined);
	}, [setFieldValue]);

	const handleCleanDate = useCallback(
		type => {
			const helpers = type === DATE_INPUT_TYPE.DEPARTURE ? departureHelpers : endHelpers;
			helpers.setTouched(false);
			helpers.setValue("");

			if (type === DATE_INPUT_TYPE.DEPARTURE) {
				endHelpers.setValue("");
				endHelpers.setTouched(false);
				setState(prevState => ({
					...prevState,
					isDateCleaned: {
						...prevState.isDateCleaned,
						departure: true,
					},
				}));
			} else {
				departureHelpers.setTouched(false);
				setState(prevState => ({
					...prevState,
					isDateCleaned: {
						...prevState.isDateCleaned,
						end: true,
					},
					calendarKey: prevState.calendarKey + 1,
				}));
			}

			resetDuration();
		},
		[departureHelpers, endHelpers, resetDuration]
	);

	const handleActiveStartDateChange = useCallback(({ activeStartDate, action }) => {
		if (action === "next" || action === "prev") {
			setState(prevState => ({
				...prevState,
				startDate: new Date(activeStartDate),
			}));
		}
	}, []);

	const handleDateClick = (
		isOtherDateClicked,
		setCurrentDateClicked,
		setOtherDateClicked,
		otherHelpers,
		otherMeta
	) => {
		setCurrentDateClicked(prev => !prev);
		if (otherMeta.touched && !otherMeta.error) {
			otherHelpers.setTouched(false);
		}

		if (isOtherDateClicked) {
			setOtherDateClicked(false);
		}
	};

	const onDepartureDateClick = useCallback(
		e => {
			e.stopPropagation();
			//When we clean departureDate the div is also clicked so to avoid the double call we check isDepartureDateCleaned
			if (!state.isDateCleaned.departure) {
				handleDateClick(
					state.isDateClicked.end,
					() =>
						setState(prevState => ({
							...prevState,
							isDateClicked: {
								...prevState.isDateClicked,
								departure: true,
							},
							calendarKey: prevState.calendarKey + 1,
						})),
					() =>
						setState(prevState => ({
							...prevState,
							isDateClicked: {
								...prevState.isDateClicked,
								end: !prevState.isDateClicked.end,
							},
						})),
					endHelpers,
					endMeta
				);
			}
		},
		[state.isDateCleaned.departure, state.isDateClicked.end, handleDateClick]
	);

	const onEndDateClick = useCallback(
		e => {
			e.stopPropagation();
			//When we clean endDate the div is also clicked so to avoid the double call we check isEndDateCleaned
			if (!state.isDateCleaned.end) {
				handleDateClick(
					state.isDateClicked.departure,
					() =>
						setState(prevState => ({
							...prevState,
							isDateClicked: {
								...prevState.isDateClicked,
								end: true,
							},
							calendarKey: prevState.calendarKey + 1,
						})),
					() =>
						setState(prevState => ({
							...prevState,
							isDateClicked: {
								...prevState.isDateClicked,
								departure: !prevState.isDateClicked.departure,
							},
						})),
					departureHelpers,
					departureMeta
				);
			}
		},
		[state.isDateCleaned.end, state.isDateClicked.departure, handleDateClick, departureHelpers]
	);

	const onDepartureDateBlur = useCallback(() => {
		if (isDepartureDateTouched) {
			setState(prevState => ({
				...prevState,
				isDateClicked: {
					...prevState.isDateClicked,
					departure: false,
				},
			}));
		}
		if (departureMeta.touched) {
			departureHelpers.setTouched(false);
		}
		if (state.isDateCleaned.departure) {
			setState(prevState => ({
				...prevState,
				isDateCleaned: {
					...prevState.isDateCleaned,
					departure: false,
				},
			}));
		}
	}, [isDepartureDateTouched, state.isDateCleaned.departure, departureHelpers]);

	const onEndDateBlur = useCallback(() => {
		if (isEndDateTouched) {
			setState(prevState => ({
				...prevState,
				isDateClicked: {
					...prevState.isDateClicked,
					end: false,
				},
			}));
		}
		if (endMeta.touched) {
			endHelpers.setTouched(false);
		}
		if (state.isDateCleaned.end) {
			setState(prevState => ({
				...prevState,
				isDateCleaned: {
					...prevState.isDateCleaned,
					end: false,
				},
			}));
		}
		onDepartureDateBlur();
	}, [isEndDateTouched, state.isDateCleaned.end, endHelpers, onDepartureDateBlur]);

	const { context, x, y, strategy, refs } = useFloating({
		placement: "bottom",
		whileElementsMounted: autoUpdate,
		open,
		onOpenChange: open => {
			setOpen(open);
			if (open) {
				onFocus();
			}
		},
		middleware: [offset(), shift()],
	});

	const resetDateTouched = useCallback(() => {
		departureHelpers.setTouched(false);
		endHelpers.setTouched(false);
	}, [departureHelpers, endHelpers]);

	const clearDeparture = useCallback(() => {
		departureHelpers.setValue("");
		resetDateTouched();
		resetDuration();
	}, [departureHelpers, resetDateTouched, resetDuration]);

	useEffect(() => {
		const departureTouched = departureMeta.touched && !departureMeta.error;
		const endTouched = endMeta.touched && !endMeta.error;
		const checkDepartureDate = departureTouched || state.isDateClicked.departure;
		const checkEndDate = endTouched || state.isDateClicked.end;
		const departureValue = departureField?.value;
		const endValue = endField?.value;

		if (!open) {
			// Reset date clicked flags
			setState(prev => ({
				...prev,
				isDateClicked: { departure: false, end: false },
			}));

			resetDateTouched();

			// Clear departure if only departure is set and none are selected
			if (departureValue && !endValue && !checkDepartureDate && !checkEndDate) {
				clearDeparture();
			}
			return;
		}

		// Open is true

		// Auto focus on departure if not set
		if (!departureValue) {
			setState(prev => ({
				...prev,
				isDateClicked: { ...prev.isDateClicked, departure: true },
			}));
		}

		// Clear departure if open just closed and no selection
		if (departureValue && !endValue && !checkDepartureDate && !checkEndDate && !open) {
			clearDeparture();
		}

		// Force calendar open if only one date is checked
		if (
			!isSubmitting &&
			((checkDepartureDate && !checkEndDate) || (!checkDepartureDate && checkEndDate))
		) {
			setOpen(true);
		}

		// Auto focus on departure field
		if (
			checkDepartureDate &&
			!checkEndDate &&
			document.activeElement !== departureDateRef.current
		) {
			departureDateRef?.current?.focus();
			departureHelpers.setTouched(true);
			setState(prev => ({
				...prev,
				isDateClicked: { ...prev.isDateClicked, departure: true },
			}));
		}

		// Auto focus on end field
		if (!checkDepartureDate && checkEndDate && document.activeElement !== endDateRef.current) {
			endDateRef?.current?.focus();
			setState(prev => ({
				...prev,
				isDateClicked: { ...prev.isDateClicked, end: true },
			}));
		}
	}, [
		open,
		departureField?.value,
		endField?.value,
		departureMeta.touched,
		departureMeta.error,
		endMeta.touched,
		endMeta.error,
		state.isDateClicked.departure,
		state.isDateClicked.end,
		endDateRef?.current,
		departureDateRef?.current,
	]);

	const dismiss = useDismiss(context);
	const click = useClick(context);
	// Gère le click et le focus par clavier
	const focus = useFocus(context, {
		keyboardOnly: false,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click, focus]);
	const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
		duration: 250,
		initial: {
			opacity: 0,
			transform: "translateX(20px)",
		},
		open: {
			opacity: 1,
			transform: "translateX(0)",
		},
		close: {
			opacity: 0,
			transform: "translateX(20px)",
		},
	});
	const floatingElementRef = useRef(null);

	const setFloatingRef = useCallback(
		node => {
			refs.setFloating(node);
			floatingElementRef.current = node;
		},
		[refs]
	);

	useEffect(() => {
		const handleScroll = () => {
			if (floatingElementRef.current && !isAutoScrollActive) {
				const { top, height } = floatingElementRef.current.getBoundingClientRect();
				const windowHeight = window.innerHeight;

				const calendarMidpoint = top + height / 2;

				const isHalfCalendarVisible =
					calendarMidpoint > 0 && calendarMidpoint < windowHeight;

				if (!isHalfCalendarVisible && open) {
					if (
						(departureField.value && !endField.value) ||
						(!departureField.value && !endField.value)
					) {
						departureHelpers.setValue("");
						departureHelpers.setTouched(false);
						endHelpers.setTouched(false);
						setState(prevState => ({
							...prevState,
							isDateClicked: {
								end: false,
								departure: false,
							},
						}));
					} else if (departureField.value && endField.value) {
						onEndDateBlur();
					}
					setOpen(false);
				}
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [
		open,
		onEndDateBlur,
		departureField.value,
		endHelpers,
		departureHelpers,
		endField.value,
		floatingElementRef?.current,
		isAutoScrollActive,
	]);

	useLayoutEffect(() => {
		// need to call this hook if open changes to trigger the update of the floating position
		if (open) {
			updateFloatingPositionReference(refs);
		}
	}, [refs, open]);

	useLayoutEffect(() => {
		if (!open) {
			closedContainerWidthRef.current = containerRef.current?.offsetWidth;

			const form = containerRef.current?.closest(".search-form__form");
			if (containerRef.current && form) {
				const containerLeft = containerRef.current.getBoundingClientRect().left;
				const formRight = form.getBoundingClientRect().right;
				// The card's border-box right edge reaches the moteur border (its left:-16px and the
				// form's 16px right padding cancel out); -16px leaves the card its own 16px right
				// padding, so the return field lines up with the moteur's content edge.
				desktopFloatingWidthRef.current = formRight - containerLeft - 16;
			}
		}
	}, [open]);

	// pour les tests e2e + suppression du hover sur les dates antérieures au départ
	const getTileClassName = useCallback(
		({ date }) => {
			const baseClass = `date-calendar-input-tile__tile date-calendar-input-tile__tile--${
				date.getMonth() + 1
			}-${date.getDate()}`;
			if (
				departureField?.value &&
				!endField?.value &&
				isBefore(date, new Date(departureField.value))
			) {
				return `${baseClass} date-calendar-input-tile__tile--before-departure`;
			}
			return baseClass;
		},
		[departureField?.value, endField?.value]
	);

	const renderTileContent = useCallback(
		({ date, view }) => {
			if (view !== "month") {
				return null;
			}

			const isDepartureDate =
				departureField?.value && isSameDay(date, new Date(departureField.value));
			const isEndDate = endField?.value && isSameDay(date, new Date(endField.value));

			if (isDepartureDate || isEndDate) {
				return <FlightOutboundFilledIcon size={ICON_SIZES.XS} />;
			}
			return null;
		},
		[departureField?.value, endField?.value]
	);
	const departureDateLabel = !isEmpty(departureLabel) ? (
		departureLabel
	) : (
		<FormattedMessage id="sdp.search.departure.date.side.panel.title" />
	);

	const returnDateLabel = !isEmpty(returnLabel) ? (
		returnLabel
	) : (
		<FormattedMessage id="sdp.search.destination.date.side.panel.title" />
	);

	return (
		<>
			<div
				className="date-calendar-input__container search-form__field-calendar-container"
				ref={containerRef}
			>
				<div
					className={`${inputClassNameDeparture} search-form__field-dates-departure`}
					onClick={() => {
						// clear dates errors to remove the error message so tha calendar is correctly displayed below the fields
						setErrors({
							travelDates: {
								departureDate: "",
								endDate: "",
							},
						});
						setOpen(true);
					}}
					ref={selectRef}
					data-testid={`${dataTestId}-departure-label`}
				>
					{!loading ? (
						<>
							<label className="date-calendar-input__label date-calendar-input__label-showpiece">
								{isDepartureDateTouched ? (
									<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL}>
										{departureDateLabel}
									</Typography>
								) : (
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										{departureDateLabel}
									</Typography>
								)}
							</label>
							<div
								className="date-calendar-input__input"
								data-testid={`${dataTestId}-departure-value`}
							>
								{departureField.value && (
									<FormattedDate
										value={departureField.value}
										weekday="short"
										day="2-digit"
										month="short"
									/>
								)}
							</div>
						</>
					) : (
						<div className="date-calendar-input__loader">
							<LoaderBar height={10} width={"70%"} />
							<LoaderBar height={10} width={"90%"} />
						</div>
					)}
				</div>
				<div
					className={`${inputClassNameReturn} search-form__field-dates-return`}
					onClick={() => setOpen(true)}
					data-testid={`${dataTestId}-return-label`}
				>
					{!loading ? (
						<>
							<label className="date-calendar-input__label date-calendar-input__label-showpiece">
								{isEndDateTouched ? (
									<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL}>
										{returnDateLabel}
									</Typography>
								) : (
									<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
										{returnDateLabel}
									</Typography>
								)}
							</label>
							<div
								className="date-calendar-input__input"
								data-testid={`${dataTestId}-return-value`}
							>
								{endField.value && (
									<FormattedDate
										value={endField.value}
										weekday="short"
										day="2-digit"
										month="short"
									/>
								)}
							</div>
						</>
					) : (
						<div className="date-calendar-input__loader">
							<LoaderBar height={10} width={"70%"} />
							<LoaderBar height={10} width={"90%"} />
						</div>
					)}
				</div>
				{open && (
					<div
						ref={refs.setReference}
						className={classNames("date-calendar-input__floating-input-container", {
							"date-calendar-input__floating-input-container--on-focus-departure":
								state.isDateClicked.departure,
							"date-calendar-input__floating-input-container--on-focus-end":
								state.isDateClicked.end,
							"date-calendar-input--modal-opened": open,
							"date-calendar-input__floating-input-container--compact": isCompact,
						})}
						data-testid={dataTestId}
						tabIndex="0"
						{...getReferenceProps()}
						style={
							isCompact
								? { width: closedContainerWidthRef.current }
								: desktopFloatingWidthRef.current
									? { width: desktopFloatingWidthRef.current }
									: undefined
						}
					>
						<div
							className={`${inputClassNameDeparture} search-form__field-dates-departure`}
							name="departureDate"
							data-testid={`${dataTestId}-departure`}
							onClick={onDepartureDateClick}
							tabIndex="0" // Rendre le div focalisable
							onBlur={onDepartureDateBlur}
							ref={departureDateRef}
						>
							{!loading ? (
								<>
									<label htmlFor={id} className="date-calendar-input__label">
										{isDepartureDateTouched ? (
											<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL}>
												{departureDateLabel}
											</Typography>
										) : (
											<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
												{departureDateLabel}
											</Typography>
										)}
									</label>
									{!departureField.value && state.isDateClicked.departure && (
										<Typography
											className="date-calendar-input__floating-input-placeholder"
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
										>
											<FormattedMessage id="sdp.search.departure.date.input.placeholder" />
										</Typography>
									)}
									<div className="date-calendar-input__input">
										{departureField.value && (
											<FormattedDate
												value={departureField.value}
												weekday="short"
												day="2-digit"
												month="short"
											/>
										)}

										{open && departureField.value && (
											<div
												className="date-calendar-input__close_icon"
												onClick={() =>
													handleCleanDate(DATE_INPUT_TYPE.DEPARTURE)
												}
											>
												<CrossCircleFilledIcon size={ICON_SIZES.MD} />
											</div>
										)}
									</div>
								</>
							) : (
								<div className="date-calendar-input__loader">
									<LoaderBar height={10} width={"70%"} />
									<LoaderBar height={10} width={"90%"} />
								</div>
							)}
						</div>
						<div
							className={`${inputClassNameReturn} search-form__field-dates-return`}
							name="endDate"
							data-testid={`${dataTestId}-return`}
							onClick={onEndDateClick}
							onBlur={onEndDateBlur}
							tabIndex="1"
							ref={endDateRef}
						>
							{!loading ? (
								<>
									<label htmlFor={id} className="date-calendar-input__label">
										{isEndDateTouched ? (
											<Typography variant={TYPOGRAPHY_VARIANTS.XSMALL}>
												{returnDateLabel}
											</Typography>
										) : (
											<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
												{returnDateLabel}
											</Typography>
										)}
									</label>
									{!endField.value && state.isDateClicked.end && (
										<Typography
											className="date-calendar-input__floating-input-placeholder"
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
										>
											<FormattedMessage id="sdp.search.return.date.input.placeholder" />
										</Typography>
									)}
									<div className="date-calendar-input__input">
										{endField.value && (
											<FormattedDate
												value={endField.value}
												weekday="short"
												day="2-digit"
												month="short"
											/>
										)}

										{open && endField.value && (
											<div
												className="date-calendar-input__close_icon"
												onClick={() => handleCleanDate(DATE_INPUT_TYPE.END)}
											>
												<CrossCircleFilledIcon size={ICON_SIZES.MD} />
											</div>
										)}
									</div>
								</>
							) : (
								<div className="date-calendar-input__loader">
									<LoaderBar height={10} width={"70%"} />
									<LoaderBar height={10} width={"90%"} />
								</div>
							)}
						</div>
					</div>
				)}
			</div>
			{!loading && open && isMounted && (
				<FloatingPortal preserveTabOrder={false}>
					<div
						ref={setFloatingRef}
						tabIndex={-1}
						className={classNames("date-calendar-input__popover", {
							"date-calendar-input__popover--compact": isCompact,
						})}
						{...getFloatingProps({
							style: {
								position: strategy,
								left: x + (popperOffset[0] ?? 0),
								top: y + (popperOffset[1] ?? 0) - (isCompact ? 24 : 0),
								width: isCompact ? closedContainerWidthRef.current : popoverWidth,
								...transitionStyles,
							},
						})}
					>
						{isCompact ? (
							<CalendarList
								months={getMonthsYearsInDateInterval(minDate, maxDate)}
								selectedDate={compact([departureField?.value, endField?.value])}
								onDateSelect={handleCompactDateSelect}
								departureDateMin={minDate}
								departureDateMax={maxDate}
								displayCalendarIcon={displayCalendarIcon}
								checkDateAvailability={checkDateAvailability}
								heightOffset={window.innerHeight - COMPACT_CALENDAR_HEIGHT}
								isCompact
							/>
						) : (
							<Calendar
								key={state.calendarKey}
								activeStartDate={state.startDate}
								onActiveStartDateChange={handleActiveStartDateChange}
								prevLabel={<ArrowLeftIcon />}
								nextLabel={<ArrowRightIcon />}
								className="date-calendar-input__calendar"
								locale={shop}
								onChange={handleDateSelect}
								maxDate={maxDate}
								minDate={minDate}
								{...(isSaudiArabiaShop(shop) && { calendarType: "Arabic" })}
								{...(isSaudiArabiaShop(shop) && {
									formatDay: formatDayArabicCalendar,
								})}
								{...(isSaudiArabiaShop(shop) && {
									formatMonthYear: formatMonthForArabicCalendar,
								})}
								value={
									departureField?.value && endField?.value
										? [departureField.value, endField.value]
										: departureField?.value || null
								}
								showDoubleView
								selectRange
								navigationLabel={({ date }) => (
									<CalendarMonthHeadline
										monthValue={date.getMonth()}
										yearValue={date.getFullYear()}
									/>
								)}
								allowPartialRange
								defaultActiveStartDate={departureField?.value || minDate}
								onClickDay={handleClickDay}
								tileClassName={getTileClassName}
								tileDisabled={({ date }) => checkDateAvailability(date)}
								{...(displayCalendarIcon && { tileContent: renderTileContent })}
							/>
						)}

						<div
							className={classNames("date-calendar-input__calendar-footer", {
								"date-calendar-input__calendar-footer--compact": isCompact,
							})}
						>
							{(!endField.value || (endField.value && departureField.value)) && (
								<Button
									variant="primary"
									size="lg"
									className="date-calendar-input__calendar-footer-button"
									data-testid="date-calendar-input-footer-button"
									onClick={() => setOpen(false)}
									disabled={!endField.value}
								>
									<FormattedMessage id="sdp.search.departure.date.side.panel.cta" />
								</Button>
							)}
						</div>

						{open && isCalendarDisabled && calendarDisabledView}
					</div>
				</FloatingPortal>
			)}
		</>
	);
};

DateCalendarInput.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	departureDateMin: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	departureDateMax: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	icon: PropTypes.element,
	checkDateAvailability: PropTypes.func,
	popperOffset: PropTypes.array,
	isCalendarDisabled: PropTypes.bool,
	updateFloatingPositionReference: PropTypes.func,
	onFocus: PropTypes.func,
	calendarDisabledView: PropTypes.element,
	displayCalendarIcon: PropTypes.bool,
	popoverWidth: PropTypes.number,
	/** Renders a single scrollable month (CalendarList, same as mobile) instead of react-calendar's 2-month view. */
	isCompact: PropTypes.bool,
	departureDateRef: PropTypes.object,
	departureLabel: PropTypes.object,
	returnLabel: PropTypes.object,
	endDateRef: PropTypes.object,
	loading: PropTypes.bool,
	name: PropTypes.string,
	isAutoScrollActive: PropTypes.bool,
	selectRef: PropTypes.object,
	"data-testid": PropTypes.string,
};

export default memo(DateCalendarInput);
